import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Star, Send, MessageSquare } from "lucide-react";

const Feedback = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const authed = user && typeof user === "object";

  const [form, setForm] = useState({
    name: authed ? user.name : "",
    email: authed ? user.email : "",
    rating: 5,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState(null);

  const loadRecent = async () => {
    if (!authed) return;
    try {
      const { data } = await api.get("/feedback/mine");
      setRecent(data);
    } catch {
      setRecent([]);
    }
  };

  useEffect(() => {
    loadRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        rating: parseInt(form.rating, 10),
        message: form.message.trim(),
      };
      if (form.name && form.name.trim()) payload.name = form.name.trim();
      if (form.email && form.email.trim()) payload.email = form.email.trim();
      await api.post("/feedback", payload);
      toast.success(t.feedback.submitted);
      setForm((f) => ({ ...f, message: "", rating: 5 }));
      if (authed) loadRecent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 py-16" data-testid="feedback-page">
      <span className="overline">Feedback</span>
      <h1 className="font-heading text-5xl sm:text-6xl mt-3 tracking-tighter text-[#EDE7D8]">
        {t.feedback.title}
      </h1>
      <p className="text-[#B79A5A] mt-3 max-w-xl">{t.feedback.subtitle}</p>

      <form onSubmit={submit} className="mt-10 grid-card p-8 space-y-5" data-testid="feedback-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="field-label">{t.feedback.name}</label>
            <input
              className="field-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="feedback-name"
            />
          </div>
          <div>
            <label className="field-label">{t.feedback.email}</label>
            <input
              type="email"
              className="field-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="feedback-email"
            />
          </div>
        </div>

        <div>
          <label className="field-label">{t.feedback.rating}</label>
          <div className="flex items-center gap-2 mt-1" data-testid="feedback-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                type="button"
                key={n}
                onClick={() => setForm({ ...form, rating: n })}
                data-testid={`feedback-rating-${n}`}
                aria-label={`${n} stars`}
                className="p-1"
              >
                <Star
                  className={`w-6 h-6 transition-colors duration-200 ${
                    n <= form.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-[#D4AF37]/30"
                  }`}
                  strokeWidth={1.5}
                />
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="field-label">{t.feedback.message}</label>
          <textarea
            required
            rows={5}
            className="field-input resize-none"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder={t.feedback.message_placeholder}
            data-testid="feedback-message"
          />
        </div>

        <div className="flex justify-end">
          <button type="submit" className="btn-pill" disabled={submitting} data-testid="feedback-submit">
            <Send className="w-4 h-4" strokeWidth={1.5} />
            {t.feedback.submit}
          </button>
        </div>
      </form>

      {authed && (
        <section className="mt-12" data-testid="feedback-recent">
          <span className="overline">{t.feedback.recent_title}</span>
          <div className="mt-4 space-y-3">
            {recent === null ? (
              <p className="text-[#B79A5A]">{t.common.loading}</p>
            ) : recent.length === 0 ? (
              <p className="text-[#B79A5A]">{t.feedback.empty}</p>
            ) : (
              recent.map((f) => (
                <div key={f.id} className="grid-card p-6" data-testid={`feedback-item-${f.id}`}>
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-3.5 h-3.5 ${
                            n <= f.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-[#D4AF37]/25"
                          }`}
                          strokeWidth={1.5}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-[#B79A5A] ml-auto">
                      {new Date(f.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-[#EDE7D8]/85 mt-3 leading-relaxed whitespace-pre-line">
                    {f.message}
                  </p>
                </div>
              ))
            )}
          </div>
        </section>
      )}
    </main>
  );
};

export default Feedback;
